/**
 * French dictionary. Every user-facing enum label lives here so the console can be
 * translated by adding a sibling dictionary with the same shape (see `useT`).
 */
export const fr = {
  app: { name: 'SaaS Manager' },
  nav: {
    dashboard: 'Vue d’ensemble',
    alerts: 'Alertes',
    incidents: 'Incidents',
    security: 'Sécurité',
    backups: 'Sauvegardes',
    connectors: 'Connecteurs',
    users: 'Utilisateurs',
    audit: 'Journal d’audit',
    settings: 'Réglages',
    projects: 'Projets',
    roadmap: 'Prochaines phases',
  },
  projectStatus: { idea: 'Idée', development: 'Développement', testing: 'Test', production: 'Production', maintenance: 'Maintenance', archived: 'Archivé' },
  health: { operational: 'Opérationnel', degraded: 'Dégradé', down: 'Indisponible', unknown: 'Inconnu' },
  integration: { draft: 'Brouillon', incomplete: 'À compléter', validated: 'Intégration validée', disconnected: 'Déconnecté' },
  criticality: { low: 'Faible', medium: 'Moyenne', high: 'Élevée', critical: 'Critique' },
  envKind: { development: 'Développement', staging: 'Préproduction', production: 'Production', other: 'Autre' },
  componentKind: { service: 'Service', database: 'Base de données', storage: 'Stockage', queue: 'File', other: 'Autre' },
  backupStatus: {
    scheduled: 'Planifiée',
    pending: 'En attente',
    running: 'En cours',
    succeeded: 'Réussie',
    partial: 'Partielle',
    failed: 'Échouée',
    cancelled: 'Annulée',
    expired: 'Expirée',
  },
  restoreStatus: {
    draft: 'Brouillon',
    prechecked: 'Vérifiée',
    queued: 'En file',
    running: 'En cours',
    succeeded: 'Restaurée',
    failed: 'Échouée',
    interrupted: 'Interrompue',
    cancelled: 'Annulée',
  },
  operationStatus: { queued: 'En file', running: 'En cours', succeeded: 'Réussie', partial: 'Partielle', failed: 'Échouée', cancelled: 'Annulée', interrupted: 'Interrompue — à diagnostiquer' },
  operationType: {
    backup: 'Sauvegarde',
    restore: 'Restauration',
    stack_analysis: 'Analyse de stack',
    changelog_sync: 'Vérification du changelog et du dossier Doc',
    connection_test: 'Test de connexion',
    retention: 'Conservation',
    console_backup: 'Sauvegarde de la console',
    deploy: 'Déploiement',
    security_scan: 'Analyse de sécurité',
    error_sync: 'Synchronisation des erreurs',
    env_inventory: 'Inventaire des variables',
  },
  severity: { info: 'Information', warning: 'Avertissement', critical: 'Critique' },
  alertStatus: { open: 'Ouverte', acknowledged: 'Acquittée', resolved: 'Résolue' },
  connectionStatus: { untested: 'Non testée', connected: 'Connectée', error: 'En erreur', revoked: 'Révoquée' },
  role: { owner: 'Propriétaire', admin: 'Administrateur', member: 'Membre' },
  capability: {
    inventory: 'Inventaire',
    source: 'Lecture de dépôt',
    health: 'Santé',
    backup: 'Sauvegarde',
    restore: 'Restauration',
    storage: 'Stockage',
    deploy: 'Déploiement',
    logs: 'Journaux',
    costs: 'Coûts',
    notify: 'Notifications',
    dns: 'DNS',
    security: 'Sécurité',
    pipelines: 'Pipelines',
    errors: 'Erreurs',
  },
  action: {
    'project.read': 'Consulter le projet',
    'project.update': 'Modifier le projet',
    'project.archive': 'Archiver',
    'project.access.manage': 'Gérer les accès',
    'changelog.manage': 'Gérer le changelog',
    'repository.manage': 'Gérer les dépôts',
    'environment.manage': 'Gérer les environnements',
    'environment.read': 'Consulter l’environnement',
    'secret.reveal': 'Afficher les secrets',
    'secret.manage': 'Gérer les secrets',
    'backup.read': 'Consulter les sauvegardes',
    'backup.run': 'Lancer une sauvegarde',
    'backup.delete': 'Supprimer une sauvegarde',
    'backup.policy.manage': 'Gérer les politiques',
    'restore.run': 'Restaurer',
    'deploy.run': 'Déployer',
    'domain.manage': 'Gérer les domaines',
    'monitor.manage': 'Gérer la surveillance',
    'alert.ack': 'Acquitter les alertes',
    'security.manage': 'Traiter les constats de sécurité',
    'incident.manage': 'Gérer les bugs et incidents',
  } as Record<string, string>,
  checkStatus: { ok: 'Vérifié', warning: 'Avertissement', blocking: 'Bloquant', unverified: 'Non vérifié' },
  findingStatus: { open: 'Ouvert', in_progress: 'En traitement', fixed: 'Corrigé', false_positive: 'Faux positif', accepted: 'Risque accepté' },
  findingSeverity: { low: 'Faible', medium: 'Moyenne', high: 'Élevée', critical: 'Critique' },
  findingCategory: { dependency: 'Dépendance vulnérable', secret: 'Secret exposé', code: 'Analyse de code', certificate: 'Certificat', access: 'Accès trop large', configuration: 'Configuration exposée' },
  posture: { secure: 'Sécurisé', at_risk: 'À risque', critical: 'Critique', unknown: 'Non évalué' },
  issueStatus: { new: 'Nouveau', analyzing: 'En analyse', in_progress: 'En cours', resolved: 'Résolu', verified: 'Vérifié' },
  incidentSeverity: { minor: 'Mineur', major: 'Majeur', critical: 'Critique' },
  errorLevel: { debug: 'Débogage', info: 'Information', warning: 'Avertissement', error: 'Erreur', fatal: 'Fatale' },
  pipelineStatus: { queued: 'En file', running: 'En cours', succeeded: 'Réussi', failed: 'Échoué', cancelled: 'Annulé', skipped: 'Ignoré' },
  linkKind: { app: 'Application', admin: 'Administration', repository: 'Dépôt', hosting: 'Hébergement', logs: 'Journaux', pipeline: 'Pipeline', tracking: 'Suivi', other: 'Lien' },
} as const;

export type Dictionary = typeof fr;
export const useT = (): Dictionary => fr;
